import type {MeetingPanelType, MeetingPanelsState} from '../types';

export enum PanelAnimationTypes {
    NONE = 'none',
    BREAKOUT_ROOMS_IN = 'breakout-rooms-in',
    BREAKOUT_ROOMS_OUT = 'breakout-rooms-out',
    CHAT_IN = 'chat-in',
    CHAT_OUT = 'chat-out',
    PARTICIPANTS_IN = 'participants-in',
    PARTICIPANTS_OUT = 'participants-out',
    PLUGINS_IN = 'plugins-in',
    PLUGINS_OUT = 'plugins-out',
    TRANSCRIPTS_IN = 'transcripts-in',
    TRANSCRIPTS_OUT = 'transcripts-out',
}

export const calculatePanelAnimation = (state: {
    currentBreakoutRooms: boolean;
    currentChat: boolean;
    currentParticipants: boolean;
    currentPlugins: boolean;
    currentTranscripts: boolean;
    nextBreakoutRooms: boolean;
    nextChat: boolean;
    nextParticipants: boolean;
    nextPlugins: boolean;
    nextTranscripts: boolean;
}) => {
    const {
        currentBreakoutRooms,
        currentChat,
        currentParticipants,
        currentPlugins,
        currentTranscripts,
        nextBreakoutRooms,
        nextChat,
        nextParticipants,
        nextPlugins,
        nextTranscripts,
    } = state;

    const areAllPanelsClosed =
        !currentChat &&
        !currentParticipants &&
        !currentBreakoutRooms &&
        !currentPlugins &&
        !currentTranscripts;
    const shouldPanelBeOpen =
        nextChat ||
        nextParticipants ||
        nextBreakoutRooms ||
        nextPlugins ||
        nextTranscripts;

    const shouldOpenBreakoutRooms = () =>
        areAllPanelsClosed && nextBreakoutRooms;
    const shouldOpenChat = () => areAllPanelsClosed && nextChat;
    const shouldOpenParticipants = () => areAllPanelsClosed && nextParticipants;
    const shouldOpenPlugins = () => areAllPanelsClosed && nextPlugins;
    const shouldOpenTranscripts = () => areAllPanelsClosed && nextTranscripts;

    const shouldCloseBreakoutRooms = () =>
        currentBreakoutRooms && !shouldPanelBeOpen;
    const shouldCloseChat = () => currentChat && !shouldPanelBeOpen;
    const shouldCloseParticipants = () =>
        currentParticipants && !shouldPanelBeOpen;
    const shouldClosePlugins = () => currentPlugins && !shouldPanelBeOpen;
    const shouldCloseTranscripts = () =>
        currentTranscripts && !shouldPanelBeOpen;

    if (shouldOpenChat()) {
        return PanelAnimationTypes.CHAT_IN;
    }

    if (shouldOpenParticipants()) {
        return PanelAnimationTypes.PARTICIPANTS_IN;
    }

    if (shouldOpenBreakoutRooms()) {
        return PanelAnimationTypes.BREAKOUT_ROOMS_IN;
    }

    if (shouldOpenPlugins()) {
        return PanelAnimationTypes.PLUGINS_IN;
    }

    if (shouldCloseChat()) {
        return PanelAnimationTypes.CHAT_OUT;
    }

    if (shouldCloseParticipants()) {
        return PanelAnimationTypes.PARTICIPANTS_OUT;
    }

    if (shouldCloseBreakoutRooms()) {
        return PanelAnimationTypes.BREAKOUT_ROOMS_OUT;
    }

    if (shouldClosePlugins()) {
        return PanelAnimationTypes.PLUGINS_OUT;
    }

    if (shouldOpenTranscripts()) {
        return PanelAnimationTypes.TRANSCRIPTS_IN;
    }

    if (shouldCloseTranscripts()) {
        return PanelAnimationTypes.TRANSCRIPTS_OUT;
    }

    return PanelAnimationTypes.NONE;
};

const calculateNextState = (
    panel: MeetingPanelType,
    {
        openChatPanel,
        openParticipantPanel,
        openBreakoutRoomsPanel,
        openPluginsPanel,
        openTranscriptsPanel,
    }: {
        openChatPanel: boolean;
        openParticipantPanel: boolean;
        openBreakoutRoomsPanel: boolean;
        openPluginsPanel: boolean;
        openTranscriptsPanel: boolean;
    },
) => {
    if (panel === 'chat') {
        return !openChatPanel;
    }
    if (panel === 'participants') {
        return !openParticipantPanel;
    }
    if (panel === 'plugins') {
        return !openPluginsPanel;
    }
    if (panel === 'transcripts') {
        return !openTranscriptsPanel;
    }
    return !openBreakoutRoomsPanel;
};

/**
 * @returns `open` to indicate if one of the panels is open
 */
export const togglePanel =
    (panel: MeetingPanelType) =>
    (panelsState: MeetingPanelsState): [boolean, PanelAnimationTypes] => {
        const isOpen = calculateNextState(panel, {
            openChatPanel: panelsState.openChatPanel,
            openParticipantPanel: panelsState.openParticipantPanel,
            openBreakoutRoomsPanel: panelsState.openBreakoutRoomsPanel,
            openPluginsPanel: panelsState.openPluginsPanel,
            openTranscriptsPanel: panelsState.openTranscriptsPanel,
        });

        const isChatToShow = panel === 'chat' && isOpen;
        const isParticipantsToShow = panel === 'participants' && isOpen;
        const isBreakoutRoomsToShow = panel === 'breakoutRooms' && isOpen;
        const isPluginsToShow = panel === 'plugins' && isOpen;
        const isTranscriptsToShow = panel === 'transcripts' && isOpen;

        const animationType = calculatePanelAnimation({
            currentBreakoutRooms: panelsState.openBreakoutRoomsPanel,
            currentChat: panelsState.openChatPanel,
            currentParticipants: panelsState.openParticipantPanel,
            currentPlugins: panelsState.openPluginsPanel,
            currentTranscripts: panelsState.openTranscriptsPanel,
            nextBreakoutRooms: isBreakoutRoomsToShow,
            nextChat: isChatToShow,
            nextParticipants: isParticipantsToShow,
            nextPlugins: isPluginsToShow,
            nextTranscripts: isTranscriptsToShow,
        });

        return [isOpen, animationType];
    };
